import type { AccountingAccountResource } from "../../accounting-account";
import type { CustomerResource } from "../../customer";
import type { SupplierResource } from "../../supplier";
import type { DocumentResource } from "../../document";
import type { BankAccountResource } from "../../bank-account";
import type { BankTransactionResource } from "../../bank-transaction";
import type { OwnerResource } from "../../owner";
import type { MediaAttributes } from "../../../common.ts";
import type { NoteResource } from "../../note";
import type { CurrencyResource } from "../../currency";

export enum AccountingPostingDocumentTypeEnum {
  Standard = "standard",
  EInvoice = "e_invoice",
}

export enum EntryType {
  Debit = "soll",
  Credit = "haben",
}
export type PositionResource = {
  id: string;
  position_id: string;
  position_number?: number | null;
  accounting_posting_id: string;
  amount_in_currency: number;
  amount: number;
  accounting_account_id: string;
  accounting_account: AccountingAccountResource | null;
  supplier_id: string;
  customer_id: string;
  document_id: string;
  bank_transaction_id: string;
  bankTransactions?: BankTransactionResource[];
  customers?: CustomerResource[];
  documents?: DocumentResource[];
  suppliers?: SupplierResource[];
  accounting_posting_positions?: AccountingPostingPositionResource[];
  accounting_posting?: AccountingPostingResource;
  external_document_number?: string | null;
  external_customer_number?: string | null;

  accounting_posting_balance?: AccountingPostingBalanceResource;
  accounting_posting_balance_id: string | null;
};

export type AccountingPostingPositionResource = PositionResource;

/**
 * Fremdbeleg: Originalreferenz des Lieferanten. Ohne Positionsbezug ist es der
 * Rechnungsbezug einer direkt aus der Bank erzeugten Buchung (M1/M2); dann trägt er die
 * bekannten Rechnungsdaten und – nach Bestätigung – den steuerlichen Zeitpunkt.
 */
export type AccountingPostingExternalDocumentResource = {
  id?: string;
  number: string;
  accounting_posting_id: string;
  accounting_posting_position_id: string | null;
  supplier_id?: string | null;
  supplier_name?: string | null;
  invoice_date?: string | null;
  currency_id?: string | null;
  net_amount?: string | null;
  vat_amount?: string | null;
  gross_amount?: string | null;
  tax_date?: string | null;
  effective_tax_date?: string | null;
  tax_date_note?: string | null;
  tax_date_confirmed?: boolean;
  confirmed_at?: string | null;
  confirmed_by_name?: string | null;
  accounting_posting?: AccountingPostingResource;
  accounting_posting_position?: AccountingPostingPositionResource;
};

/** Rechnungsdaten, die mit einer Bankbuchung gespeichert werden (M1/M2). */
export type InvoiceReferenceRequestBody = {
  number?: string | null;
  supplier_id?: string | null;
  invoice_date?: string | null;
  currency_id?: string | null;
  net_amount?: number | string | null;
  vat_amount?: number | string | null;
  gross_amount?: number | string | null;
  tax_date?: string | null;
  tax_date_note?: string | null;
  confirm_tax_date?: boolean;
};

/** Bereits erfasste Vorgänge derselben Rechnung (M1). */
export type InvoiceDuplicateMatch = {
  accounting_posting_id: string;
  posting_number: string | null;
  kind: "same_invoice" | "possible";
  reasons: string[];
};

export type AccountingPostingResource = {
  id: string;
  gid: string;
  owner_id: string;
  number: string;
  currency?: CurrencyResource | null;
  posting_number: string; // todo: it is the same as number field. why we need to identical fields?
  source_document_date: string;
  currency_id: string;
  currency_rate: number;
  is_blocked: boolean;
  description: string;
  accounting_posting_positions: AccountingPostingPositionResource[];
  sepa?: AccountingPostingSepaResource;
  notes?: NoteResource[];
  accounting_posting_external_document?: AccountingPostingExternalDocumentResource;
  invoice_reference?: AccountingPostingExternalDocumentResource | null;
  accounting_posting_documents?: {
    accounting_posting_id: string;
    created_at: string;
    id: string;
    type: AccountingPostingDocumentTypeEnum;
    file: MediaAttributes;
  }[];
  owner?: OwnerResource;
};

export type AccountingPostingSepaResource = {
  id: string;
  remote_name: null | string;
  bic: null | string;
  iban: string | null;
  bank_sepa_id: string | null;
  is_paypal: boolean;
  amount: string | null;
  bank_account_id: string | null;
  bankAccount?: BankAccountResource;
  accounting_posting_id: string;
  accountingPosting?: AccountingPostingResource;
};

export type AccountingPostingBalanceResource = {
  id: string;
  gid: string;
  // saldo: string;
  paid_amount: string;
  balance: string;
  accounting_posting_position?: AccountingPostingPositionResource;
  accounting_posting?: AccountingPostingResource;
  accounting_posting_id: string;
  accounting_posting_position_id: string;
};

export enum AccountingPostingListFilters {
  ShowAdvancePaymentsReceived = "show_advance_payments_received",
}
